import { TEXTS } from '../../core/texts';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import InputWithIcon from '../common/InputWithIcon';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ButtonWithLoader } from '../common/ButtonWithLoader';
import { AUTH_PATH } from '../../router/paths';
import { ValidationErrorState } from '../../types/validationErrorState';
import { Validator } from '../../core/Validator';
import { useNotification } from '../../+state/useNotification';
import { useMutation } from '@tanstack/react-query';
import { AdminAuthService } from '../../services/admin-auth';
import { ISetPasswordProps } from '../../interfaces/setPasswordProps';
import { AxiosErrorResponse } from '../../interfaces/errorResponse';
import { parseAxiosError } from '../../services/axios';

const ResetPassword = () => {
  const [validErrors, setValidErrors] = useState<ValidationErrorState>({});
  const [forceNavigate, setForceNavigate] = useState(false);
  const { openErrorToast } = useNotification();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token'), [searchParams]);
  const tokenId = useMemo(() => searchParams.get('tokenId'), [searchParams]);

  useEffect(() => {
    if (!token || !tokenId) {
      openErrorToast(TEXTS.MISSING_TOKEN);
      setForceNavigate(true);
    }
  }, [openErrorToast, token, tokenId]);

  const mutation = useMutation({
    mutationFn: (data: ISetPasswordProps) => AdminAuthService.setPassword(data),
    onSuccess: () => setForceNavigate(true),
    onError: (err: AxiosErrorResponse) => setValidErrors({ password: parseAxiosError(err) })
  });

  const submitFormHandler = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const password = (e.target as any).password?.value || '';
    const validationErrors = Validator.validate({ password }, Validator.NAMES.AUTH);
    if (validationErrors) {
      return setValidErrors(validationErrors);
    }
    mutation.mutate({
      password,
      token: searchParams.get('token') as string,
      tokenId: searchParams.get('tokenId') as string,
      reset: true
    });
  }, [searchParams, mutation]);

  return (
    <>
      <Typography variant={'h2'}>{TEXTS.RESET_PASSWORD}</Typography>
      <form onSubmit={submitFormHandler}>
        <InputWithIcon name={'password'} autoComplete={'current-password'} placeholder={TEXTS.NEW_PASSWORD}
                       type={'password'} sx={{ my: 2 }} error={!!validErrors['password']}
                       helperText={validErrors['password']} icon={'lock'}
        />
        <ButtonWithLoader type={'submit'} variant={'contained'} loading={mutation.isLoading}>
          {TEXTS.CONFIRM}
        </ButtonWithLoader>
      </form>
      {
        forceNavigate && <Navigate to={AUTH_PATH}/>
      }
    </>
  );
};

export default ResetPassword;